import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, Phone, Sparkles } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { PageCTA } from "@/components/PageCTA";
import { StickyLocalCTA } from "@/components/StickyLocalCTA";
import { StaticReviews } from "@/components/StaticReviews";
import heroColorado from "@/assets/hero-colorado.jpg";
import { industryHeroAlt } from "@/lib/imageAlt";
import {
  INDUSTRY_PAGE_SLUGS,
  getIndustryPage,
  type IndustryPage,
} from "@/lib/industriesData";
import { INDUSTRY_SERVICES, getIndustryAnchorVariant } from "@/lib/industryServiceData";
import { ogImageFor, ogMetaTags } from "@/lib/ogImages";
import { CitiesForIndustryLinks } from "@/components/CrossLinkMesh";

const SITE_URL = "https://coloradowebimpressions.com";
const PHONE = "+1-719-419-3935";
const HQ_ADDRESS = {
  "@type": "PostalAddress" as const,
  streetAddress: "3210 E Woodmen Rd STE 110",
  addressLocality: "Colorado Springs",
  addressRegion: "CO",
  postalCode: "80920",
  addressCountry: "US",
};
const AREA_SERVED = {
  "@type": "State" as const,
  name: "Colorado",
};

function buildFaqJsonLd(page: IndustryPage) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function buildLocalBusinessJsonLd(page: IndustryPage, canonical: string) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    // Page-scoped sub-node — linked to the canonical Organization so Google
    // merges this page's local-business signals with the main entity graph.
    "@id": `${canonical}#localbusiness`,
    parentOrganization: { "@id": `${SITE_URL}/#org` },
    name: `Colorado Web Impressions — ${page.name} Marketing`,
    description: page.metaDescription,
    url: canonical,
    telephone: PHONE,
    priceRange: "$$",
    address: HQ_ADDRESS,
    areaServed: AREA_SERVED,
  };
}

function buildServiceJsonLd(page: IndustryPage, canonical: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${page.name} Marketing in Colorado`,
    serviceType: `${page.name} Marketing`,
    description: page.metaDescription,
    provider: {
      "@type": "LocalBusiness",
      "@id": `${canonical}#localbusiness`,
      name: "Colorado Web Impressions",
    },
    areaServed: AREA_SERVED,
    url: canonical,
    audience: {
      "@type": "BusinessAudience",
      name: page.shortName,
    },
  };
}

function buildBreadcrumbJsonLd(page: IndustryPage, canonical: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Industries", item: `${SITE_URL}/industries` },
      { "@type": "ListItem", position: 3, name: page.name, item: canonical },
    ],
  };
}

export const Route = createFileRoute("/industries/$industrySlug")({
  loader: ({ params }) => {
    const page = getIndustryPage(params.industrySlug);
    if (!page) throw notFound();
    return { page };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Industry | Colorado Web Impressions" }] };
    const { page } = loaderData;
    const canonical = `${SITE_URL}/industries/${page.slug}`;
    const ogImage = `${SITE_URL}${ogImageFor({})}`;
    return {
      meta: [
        { title: page.metaTitle },
        { name: "description", content: page.metaDescription },
        { property: "og:title", content: page.metaTitle },
        { property: "og:description", content: page.metaDescription },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "website" },
        ...ogMetaTags(ogImage),
        { name: "twitter:title", content: page.metaTitle },
        { name: "twitter:description", content: page.metaDescription },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildLocalBusinessJsonLd(page, canonical)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildServiceJsonLd(page, canonical)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqJsonLd(page)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildBreadcrumbJsonLd(page, canonical)),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <SiteLayout>
      <PageHero
        eyebrow="Not found"
        title={<>Industry page not found</>}
        description="We couldn't find that industry. Browse all industries we serve below."
      >
        <Link
          to="/industries"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
        >
          See all industries
          <ArrowRight className="h-5 w-5" />
        </Link>
      </PageHero>
    </SiteLayout>
  ),
  component: IndustryDetailPage,
});

function IndustryDetailPage() {
  const { page } = Route.useLoaderData() as { page: IndustryPage };
  const heroParts = page.heroTitle.split(page.heroGoldWord);
  // Industry × service spokes — dedicated /{service}-for-{industry} pages
  // (e.g. /seo-for-hvac, /web-design-for-hvac). Held out of the sitemap for
  // non-priority industries, so this internal-link block is the primary
  // discovery path for crawlers.
  const industryServiceSpokes = Object.values(INDUSTRY_SERVICES).map((s, idx) => ({
    slug: s.slug,
    name: s.serviceName,
    href: `/${s.slug}-for-${page.slug}`,
    // Rotate anchor patterns by industry-slug length + service idx so each
    // industry hub features a different ordering of phrasings (varied anchors
    // across the site) but the same combo always renders the same anchor.
    anchor: getIndustryAnchorVariant(s.slug, page.shortName, page.slug.length + idx),
  }));

  return (
    <SiteLayout>
      <PageHero
        eyebrow={page.heroEyebrow}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Industries", to: "/industries" },
          { label: page.name },
        ]}
        title={
          <>
            {heroParts[0]}
            <span className="text-gradient-gold">{page.heroGoldWord}</span>
            {heroParts[1] ?? ""}
          </>
        }
        description={page.heroDescription}
        image={{
          src: heroColorado,
          alt: industryHeroAlt(page.name),
        }}
      >
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            {page.primaryCtaLabel}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-5 w-5" />
            (719) 419-3935
          </a>
        </div>
      </PageHero>

      {/* Industry × service spokes — surfaces the dedicated
          /{service}-for-{industry} landing pages (e.g. /seo-for-hvac,
          /web-design-for-hvac, /local-seo-for-hvac, /ppc-for-hvac).
          Rendered as <a> (not Link) because each spoke maps to the same
          dynamic file route with different params; a plain anchor is crawled
          identically and avoids the typed-Link param ceremony. Also keeps a
          link back to the parent /industries hub and to /pricing. */}
      <section className="border-b border-border bg-section-alt">
        <div className="mx-auto max-w-6xl px-5 py-5 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Marketing built for {page.shortName}s in Colorado.{" "}
              <Link
                to="/industries"
                className="font-bold text-brand-gold-deep hover:underline"
              >
                Browse all industries →
              </Link>
            </p>
            <Link
              to="/pricing"
              className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep hover:underline"
            >
              See {page.shortName} marketing pricing →
            </Link>
          </div>

          <div className="mt-4 border-t border-border pt-4">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
              Dedicated {page.shortName} service pages
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-semibold">
              {industryServiceSpokes.map((s, idx) => (
                <span key={s.slug} className="inline-flex items-center gap-3">
                  <a
                    href={s.href}
                    className="group inline-flex items-center gap-1.5 text-foreground transition-smooth hover:text-brand-gold-deep"
                  >
                    <ArrowRight className="h-3.5 w-3.5 text-brand-gold-deep transition-transform group-hover:translate-x-0.5" />
                    <span className="underline-offset-4 group-hover:underline">
                      {s.anchor}
                    </span>
                  </a>
                  {idx < industryServiceSpokes.length - 1 && (
                    <span className="text-muted-foreground/70">·</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pains */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              What we hear most
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              The {page.name.toLowerCase()} marketing problems we fix every week
            </h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {page.pains.map((p) => (
              <div
                key={p}
                className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5 shadow-card"
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-secondary text-brand-gold-deep">
                  <Sparkles className="h-4 w-4" />
                </div>
                <p className="text-sm text-foreground/90">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              How we grow {page.shortName}s in Colorado
            </h2>
            <p className="mt-3 text-muted-foreground">
              Three focused systems that work together — built around how your customers actually
              search.
            </p>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {page.whatWeDo.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Colorado city snippets */}
      <section className="bg-gradient-dark py-20 text-brand-cream lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
              Built for Colorado
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              How we tailor {page.name} marketing by city
            </h2>
            <p className="mt-3 text-brand-cream/80">
              Same playbook, different markets. Here's how we adapt the strategy across the Front
              Range and Southern Colorado.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {page.citySnippets.map((c) => (
              <div
                key={c.city}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-brand-gold" />
                  <h3 className="text-lg font-bold text-white">{c.city}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-brand-cream/80">{c.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-3xl border border-white/15 bg-black/30 p-8 text-center backdrop-blur sm:p-10">
            <h3 className="text-2xl font-extrabold text-white sm:text-3xl">
              Ready to see what's possible for your {page.shortName}?
            </h3>
            <p className="mx-auto mt-3 max-w-2xl text-brand-cream/80">
              We'll audit your current rankings, website, and Google Business Profile — and show
              you a clear, no-pressure path to more booked work.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                {page.primaryCtaLabel}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
              >
                See transparent pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {page.name} marketing FAQs
            </h2>
            <p className="mt-3 text-muted-foreground">
              Straight answers to the questions {page.shortName}s ask us most.
            </p>
          </div>
          <div className="mt-10 space-y-4">
            {page.faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <h3 className="text-base font-bold text-foreground">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-muted-foreground">
            Don't see your industry detail page yet?{" "}
            <Link
              to="/industries"
              className="font-semibold text-brand-gold-deep hover:underline"
            >
              Browse all industries we serve →
            </Link>
          </p>
        </div>
      </section>

      <CitiesForIndustryLinks industryShortName={page.shortName} />

      <StaticReviews
        eyebrow="Google reviews"
        heading="What Colorado clients are saying"
        description={`Verified Google reviews from local businesses we've helped — including ${page.shortName}s and other Colorado pros. See the full live feed on our reviews page.`}
      />

      <PageCTA
        headline={`Ready to bring more booked work to your ${page.shortName}?`}
        description={`We'll audit your current ${page.name} marketing — website, Google Business Profile, ads, and rankings — and give you a clear plan to grow. No pressure, no contracts.`}
        seeOurWork={{
          to: "/reviews",
          label: `See real reviews from Colorado ${page.shortName}s and other local businesses`,
        }}
      />
      <StickyLocalCTA context={page.shortName} />
    </SiteLayout>
  );
}

// Re-export for sitemap consumers
export { INDUSTRY_PAGE_SLUGS };
